import React from "react";
import "../../style/ZombieCrushersSection.css";

const numberFormatter = new Intl.NumberFormat("it-IT");
const decimalFormatter = new Intl.NumberFormat("it-IT", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
});
const unlockedDateFormatter = new Intl.DateTimeFormat("it-IT", {
  dateStyle: "medium",
  timeStyle: "short"
});

const STAT_LABELS = {
  total_sessions: "Sessioni giocate",
  total_kills: "Uccisioni totali",
  average_kills: "Uccisioni medie",
  max_kills: "Record uccisioni",
  best_kills: "Record uccisioni",
  total_survival_seconds: "Sopravvivenza totale",
  best_survival_seconds: "Record sopravvivenza",
  best_survival_time: "Record sopravvivenza",
  survival_seconds: "Record sopravvivenza",
  average_survival_seconds: "Sopravvivenza media",
  survival_rate: "Tasso sopravvivenza",
  win_rate: "Win rate",
  kill_death_ratio: "Rapporto K/D"
};

const RECORD_LABELS = {
  best_kills_session: "Miglior sessione (uccisioni)",
  best_survival_session: "Miglior sessione (sopravvivenza)",
  longest_streak: "Serie più lunga",
  top_clip: "Clip più vista"
};

function titleFromKey(key) {
  if (!key) {
    return "";
  }
  if (STAT_LABELS[key]) {
    return STAT_LABELS[key];
  }
  if (RECORD_LABELS[key]) {
    return RECORD_LABELS[key];
  }
  return String(key)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, match => match.toUpperCase());
}

function formatDuration(value) {
  if (value === null || value === undefined) {
    return "-";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
      return trimmed;
    }
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return formatDuration(numeric);
    }
    return trimmed;
  }

  if (!Number.isFinite(value)) {
    return "-";
  }

  const totalSeconds = Math.max(0, Math.round(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
}

function formatStatValue(key, value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "number") {
    if (key && key.includes("seconds")) {
      return formatDuration(value);
    }
    if (key && (key.includes("rate") || key.includes("ratio") || key.includes("average"))) {
      return decimalFormatter.format(value);
    }
    return numberFormatter.format(value);
  }

  if (typeof value === "string" && key && key.includes("seconds")) {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return formatDuration(numeric);
    }
  }

  return value;
}

function extractListFromObject(object, options = {}) {
  if (!object || typeof object !== "object") {
    return [];
  }

  const { filter } = options;

  return Object.entries(object)
    .filter(([key, value]) => value !== undefined && value !== null && value !== "")
    .filter(([key, value]) => {
      if (typeof filter === "function") {
        return filter(key, value);
      }
      return true;
    })
    .map(([key, value]) => ({
      label: titleFromKey(key),
      value: formatStatValue(key, value)
    }));
}

function shouldIncludeRecordField(key) {
  const normalizedKey = String(key).toLowerCase().replace(/[^a-z]/g, "");
  return normalizedKey !== "createdat";
}

function formatCount(value) {
  if (value === null || value === undefined) {
    return null;
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numberFormatter.format(numeric);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number") {
    return numberFormatter.format(value);
  }

  return String(value);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length === 0) {
        continue;
      }
      return trimmed;
    }
    return value;
  }
  return null;
}

function collectImageCandidates(...sources) {
  const candidates = [];
  const seenStrings = new Set();
  const seenObjects = new Set();

  function visit(value) {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || seenStrings.has(trimmed)) {
        return;
      }
      seenStrings.add(trimmed);
      candidates.push(trimmed);
      return;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      return;
    }
    if (value instanceof Date) {
      return;
    }
    if (typeof value !== "object") {
      return;
    }
    if (seenObjects.has(value)) {
      return;
    }
    seenObjects.add(value);

    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    Object.values(value).forEach(visit);
  }

  sources.forEach(visit);
  return candidates;
}

function safeToArray(value) {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "object") {
    return Object.values(value);
  }
  return [value];
}

function parseTimestamp(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function mergeAchievementDetails(base, next) {
  const merged = { ...base };

  if (!merged.name && next.name) {
    merged.name = next.name;
  }
  if (!merged.description && next.description) {
    merged.description = next.description;
  }

  if (!merged.icon && next.icon) {
    merged.icon = next.icon;
  }
  if (!merged.imageUrl && next.imageUrl) {
    merged.imageUrl = next.imageUrl;
  }
  if (!merged.rarity && next.rarity) {
    merged.rarity = next.rarity;
  }
  if (!merged.tier && next.tier) {
    merged.tier = next.tier;
  }
  if ((merged.points === null || merged.points === undefined) && next.points !== null && next.points !== undefined) {
    merged.points = next.points;
  }

  if (!merged.unlocked && next.unlocked) {
    merged.unlocked = true;
  }

  if (next.unlockedTimestamp != null) {
    if (
      merged.unlockedTimestamp == null ||
      next.unlockedTimestamp > merged.unlockedTimestamp
    ) {
      merged.unlockedTimestamp = next.unlockedTimestamp;
      merged.unlockedAt = next.unlockedAt;
    }
  } else if (!merged.unlockedAt && next.unlockedAt) {
    merged.unlockedAt = next.unlockedAt;
  }

  return merged;
}

function normalizeAchievementEntry(entry, forcedUnlocked) {
  if (entry === null || entry === undefined) {
    return null;
  }

  if (typeof entry === "string") {
    const code = entry.trim();
    if (!code) {
      return null;
    }
    return {
      code: code.toUpperCase(),
      name: titleFromKey(code),
      description: "",
      icon: null,
      imageUrl: null,
      unlocked: forcedUnlocked ?? false,
      unlockedAt: null,
      unlockedTimestamp: null,
      rarity: null,
      points: null,
      tier: null
    };
  }

  const container = typeof entry === "object" ? entry : {};
  const nested =
    (typeof container.achievement === "object" && container.achievement) ||
    (typeof container.details === "object" && container.details) ||
    (typeof container.data === "object" && container.data) ||
    {};

  const combined = { ...nested, ...container };

  const rawCode = firstNonEmpty(
    combined.achievement_code,
    combined.code,
    combined.slug,
    combined.id,
    combined.key
  );

  const code = rawCode ? String(rawCode).toUpperCase() : null;

  const definition = typeof combined.definition === "object" && combined.definition !== null ? combined.definition : null;
  const metadata = typeof combined.metadata === "object" && combined.metadata !== null ? combined.metadata : null;
  const detailsNode = typeof combined.details === "object" && combined.details !== null ? combined.details : null;

  const definitionName = definition
    ? firstNonEmpty(
        definition.name,
        definition.title,
        definition.display_name,
        definition.displayName
      )
    : null;
  const metadataName = metadata
    ? firstNonEmpty(
        metadata.name,
        metadata.title,
        metadata.display_name,
        metadata.displayName
      )
    : null;
  const detailsName = detailsNode
    ? firstNonEmpty(
        detailsNode.name,
        detailsNode.title,
        detailsNode.display_name,
        detailsNode.displayName
      )
    : null;
  const lockedName = firstNonEmpty(
    combined.locked_name,
    combined.lockedName,
    combined.locked_title,
    combined.lockedTitle,
    combined.locked_display_name,
    combined.lockedDisplayName,
    definition
      ? firstNonEmpty(
          definition.locked_name,
          definition.lockedName,
          definition.locked_title,
          definition.lockedTitle,
          definition.locked_display_name,
          definition.lockedDisplayName
        )
      : null,
    metadata
      ? firstNonEmpty(
          metadata.locked_name,
          metadata.lockedName,
          metadata.locked_title,
          metadata.lockedTitle,
          metadata.locked_display_name,
          metadata.lockedDisplayName
        )
      : null
  );

  const name = firstNonEmpty(
    combined.name,
    combined.title,
    combined.display_name,
    combined.displayName,
    lockedName,
    detailsName,
    definitionName,
    metadataName
  );

  const definitionDescription = definition
    ? firstNonEmpty(
        definition.description,
        definition.text,
        definition.summary,
        definition.long_description,
        definition.longDescription,
        definition.locked_description,
        definition.lockedDescription,
        definition.unlock_hint,
        definition.unlockHint,
        definition.hint
      )
    : null;
  const metadataDescription = metadata
    ? firstNonEmpty(
        metadata.description,
        metadata.text,
        metadata.summary,
        metadata.long_description,
        metadata.longDescription,
        metadata.locked_description,
        metadata.lockedDescription,
        metadata.unlock_hint,
        metadata.unlockHint,
        metadata.hint
      )
    : null;
  const detailsDescription = detailsNode
    ? firstNonEmpty(
        detailsNode.description,
        detailsNode.text,
        detailsNode.summary,
        detailsNode.long_description,
        detailsNode.longDescription,
        detailsNode.locked_description,
        detailsNode.lockedDescription,
        detailsNode.unlock_hint,
        detailsNode.unlockHint,
        detailsNode.hint
      )
    : null;
  const lockedDescription = firstNonEmpty(
    combined.locked_description,
    combined.lockedDescription,
    combined.locked_text,
    combined.lockedText,
    combined.hint,
    combined.unlock_hint,
    combined.unlockHint,
    combined.requirements_description,
    combined.requirementsDescription,
    combined.requirement_description,
    combined.requirementDescription,
    combined.goal_description,
    combined.goalDescription,
    combined.instructions
  );

  const description = firstNonEmpty(
    combined.description,
    combined.text,
    combined.summary,
    combined.long_description,
    combined.longDescription,
    detailsDescription,
    definitionDescription,
    metadataDescription,
    lockedDescription
  );

  const iconCandidates = collectImageCandidates(
    combined.locked_badge_url,
    combined.locked_badgeUrl,
    combined.locked_icon_url,
    combined.locked_iconUrl,
    combined.locked_icon,
    combined.locked_image_url,
    combined.locked_imageUrl,
    combined.locked_image,
    combined.lockedBadge,
    combined.lockedBadgeUrl,
    combined.badge_url,
    combined.badgeUrl,
    combined.icon_url,
    combined.iconUrl,
    combined.image_url,
    combined.imageUrl,
    combined.icon,
    combined.image,
    combined.asset_url,
    combined.assetUrl,
    combined.thumbnail,
    combined.thumbnail_url,
    combined.thumbnailUrl,
    combined.cover,
    combined.cover_url,
    combined.coverUrl,
    combined.artwork,
    combined.artwork_url,
    combined.artworkUrl,
    combined.media_url,
    combined.mediaUrl,
    combined.media,
    combined.badge,
    combined.badges,
    combined.images,
    combined.icons,
    combined.assets,
    combined.art,
    definition && definition.badge,
    definition && definition.images,
    definition && definition.media,
    metadata && metadata.badge,
    metadata && metadata.images,
    detailsNode && detailsNode.badge,
    detailsNode && detailsNode.images
  );
  const icon = iconCandidates.length > 0 ? iconCandidates[0] : null;

  const imageCandidates = collectImageCandidates(
    combined.image_url,
    combined.imageUrl,
    combined.cover,
    combined.cover_url,
    combined.coverUrl,
    combined.thumbnail,
    combined.thumbnail_url,
    combined.thumbnailUrl,
    combined.artwork,
    combined.artwork_url,
    combined.artworkUrl,
    combined.badge_url,
    combined.badgeUrl,
    combined.icon_url,
    combined.iconUrl,
    combined.icon,
    combined.image,
    combined.media_url,
    combined.mediaUrl,
    combined.media,
    combined.badge,
    combined.badges,
    combined.images,
    combined.icons,
    combined.assets,
    combined.art,
    definition && definition.images,
    definition && definition.media,
    metadata && metadata.images,
    metadata && metadata.media,
    detailsNode && detailsNode.images,
    icon
  );
  const imageUrl = imageCandidates.length > 0 ? imageCandidates[0] : icon;

  const unlockedAt = firstNonEmpty(
    combined.unlocked_at,
    combined.unlockedAt,
    combined.earned_at,
    combined.earnedAt
  );

  const unlockedTimestamp = parseTimestamp(unlockedAt);

  let unlockedValue =
    forcedUnlocked ??
    combined.unlocked ??
    combined.is_unlocked ??
    combined.isUnlocked ??
    (typeof combined.locked === "boolean" ? !combined.locked : undefined) ??
    (typeof combined.is_locked === "boolean" ? !combined.is_locked : undefined);

  if (unlockedValue == null && typeof combined.status === "string") {
    const status = combined.status.trim().toLowerCase();
    if (["unlocked", "completed", "earned", "achieved", "obtained"].includes(status)) {
      unlockedValue = true;
    } else if (["locked", "available", "pending", "todo", "missing"].includes(status)) {
      unlockedValue = false;
    }
  }

  if (unlockedValue == null && unlockedTimestamp != null) {
    unlockedValue = true;
  }

  const unlocked = unlockedValue == null ? false : Boolean(unlockedValue);

  const tier = firstNonEmpty(combined.tier, combined.level, combined.rank);
  const rarity = firstNonEmpty(combined.rarity, combined.type, combined.classification);
  const rawPoints = firstNonEmpty(combined.points, combined.score, combined.value);

  const normalizedPoints =
    rawPoints === null || rawPoints === undefined
      ? null
      : Number.isFinite(Number(rawPoints))
      ? Number(rawPoints)
      : rawPoints;

  const normalized = {
    code: code || (name ? titleFromKey(name) : null),
    name: name || (code ? titleFromKey(code) : null),
    description: description || "",
    icon: icon || imageUrl || null,
    imageUrl: imageUrl || icon || null,
    unlocked,
    unlockedAt: unlockedTimestamp != null ? unlockedAt : null,
    unlockedTimestamp,
    rarity: rarity ? titleFromKey(String(rarity)) : null,
    points: normalizedPoints,
    tier: tier ? titleFromKey(String(tier)) : null
  };

  if (!normalized.name && normalized.code) {
    normalized.name = titleFromKey(normalized.code);
  }

  if (!normalized.code && normalized.name) {
    normalized.code = normalized.name.toUpperCase().replace(/\s+/g, "_");
  }

  if (!normalized.code && !normalized.name) {
    return null;
  }

  return normalized;
}

function normalizeAchievementsCollection(rawAchievements) {
  const candidates = [];

  function pushCandidates(value, forcedUnlocked) {
    safeToArray(value).forEach(item => {
      if (item !== undefined && item !== null) {
        candidates.push({ item, forcedUnlocked });
      }
    });
  }

  if (Array.isArray(rawAchievements)) {
    pushCandidates(rawAchievements);
  } else if (rawAchievements && typeof rawAchievements === "object") {
    let used = false;
    const descriptors = [
      ["unlocked", true],
      ["earned", true],
      ["completed", true],
      ["locked", false],
      ["available", false],
      ["definitions", false],
      ["definition", false],
      ["catalog", false],
      ["catalogue", false],
      ["all", undefined],
      ["items", undefined],
      ["list", undefined],
      ["achievements", undefined]
    ];

    descriptors.forEach(([key, forced]) => {
      if (rawAchievements[key]) {
        pushCandidates(rawAchievements[key], forced);
        used = true;
      }
    });

    if (!used) {
      pushCandidates(rawAchievements, undefined);
    }
  }

  const deduped = new Map();

  candidates.forEach(({ item, forcedUnlocked }) => {
    const normalized = normalizeAchievementEntry(item, forcedUnlocked);
    if (!normalized) {
      return;
    }
    const key = (normalized.code || normalized.name || "").toLowerCase();
    if (!key) {
      return;
    }
    if (!deduped.has(key)) {
      deduped.set(key, normalized);
      return;
    }
    const merged = mergeAchievementDetails(deduped.get(key), normalized);
    deduped.set(key, merged);
  });

  const all = Array.from(deduped.values());

  const unlocked = [];
  const locked = [];

  all.forEach(item => {
    if (item.unlocked) {
      unlocked.push(item);
    } else {
      locked.push(item);
    }
  });

  unlocked.sort((a, b) => {
    const aTime = a.unlockedTimestamp ?? -Infinity;
    const bTime = b.unlockedTimestamp ?? -Infinity;
    if (aTime !== bTime) {
      return bTime - aTime;
    }
    return (a.name || "").localeCompare(b.name || "");
  });

  locked.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return {
    all: [...unlocked, ...locked],
    unlocked,
    locked
  };
}

function formatUnlockedDate(value) {
  if (!value) {
    return null;
  }
  const timestamp = parseTimestamp(value);
  if (timestamp == null) {
    return typeof value === "string" ? value : null;
  }
  try {
    return unlockedDateFormatter.format(new Date(timestamp));
  } catch (error) {
    return typeof value === "string" ? value : null;
  }
}

function getAchievementDisplayName(achievement) {
  if (!achievement) {
    return "";
  }
  if (achievement.name) {
    return achievement.name;
  }
  if (achievement.title) {
    return achievement.title;
  }
  return "";
}

function getAchievementInitial(displayName) {
  const source = firstNonEmpty(displayName, "?");
  return String(source).trim().charAt(0).toUpperCase() || "?";
}

function renderAchievementCard(achievement, { key, locked = false, showUnlockedMeta = false } = {}) {
  if (!achievement) {
    return null;
  }

  const displayName = getAchievementDisplayName(achievement);
  const iconSource = firstNonEmpty(achievement.imageUrl, achievement.icon);
  const fallbackInitial = getAchievementInitial(displayName);
  const rarityLabel = achievement.rarity ? titleFromKey(achievement.rarity) : null;
  const isPlaceholder = Boolean(achievement.placeholder);
  const isLocked = locked || !achievement.unlocked;

  const normalizedPoints =
    achievement.points === null || achievement.points === undefined
      ? null
      : Number.isFinite(Number(achievement.points))
      ? Number(achievement.points)
      : achievement.points;

  const cardClassNames = ["zc-achievement-card"];
  if (isLocked) {
    cardClassNames.push("zc-achievement-card--locked");
  }
  if (isPlaceholder) {
    cardClassNames.push("zc-achievement-card--placeholder");
  }

  const showLockedIconOverlay = isLocked && !isPlaceholder && Boolean(iconSource);
  const iconClassNames = ["zc-achievement-card__icon"];
  if (showLockedIconOverlay) {
    iconClassNames.push("zc-achievement-card__icon--locked");
  }

  const metaItems = [];

  if (showUnlockedMeta && !isPlaceholder) {
    const unlockedLabel = formatUnlockedDate(achievement.unlockedAt);
    if (unlockedLabel) {
      metaItems.push({ label: "Sbloccato il", value: unlockedLabel });
    }
  }

  if (!isPlaceholder && normalizedPoints !== null && normalizedPoints !== undefined && normalizedPoints !== "") {
    metaItems.push({ label: "Punti", value: normalizedPoints });
  }

  if (!isPlaceholder && rarityLabel) {
    metaItems.push({ label: "Rarità", value: rarityLabel });
  }

  if (isLocked && !showUnlockedMeta) {
    metaItems.push({ label: "Stato", value: "Da sbloccare" });
  }

  const tierLabel = achievement.tier ? titleFromKey(achievement.tier) : null;
  const statusLabel = isPlaceholder ? "Da scoprire" : null;

  return (
    <article key={key} className={cardClassNames.join(" ")}>
      <div className={iconClassNames.join(" ")}>
        {iconSource ? (
          <img
            src={iconSource}
            alt={isLocked ? "" : displayName || "Achievement"}
            aria-hidden={isLocked ? true : undefined}
          />
        ) : (
          <span className="zc-achievement-card__placeholder" aria-hidden="true">
            {fallbackInitial}
          </span>
        )}
        {isLocked && (
          <span className="zc-achievement-card__lock" aria-hidden="true">
            🔒
          </span>
        )}
      </div>
      <div className="zc-achievement-card__content">
        <div className="zc-achievement-card__header">
          <span className="zc-achievement-card__name">
            {displayName || (isPlaceholder ? "Achievement nascosto" : "Achievement")}
          </span>
          <div className="zc-achievement-card__tags">
            {tierLabel && <span className="zc-achievement-card__tag">{tierLabel}</span>}
            {statusLabel && (
              <span
                className={`zc-achievement-card__status${isLocked ? " zc-achievement-card__status--locked" : ""}`}
              >
                {statusLabel}
              </span>
            )}
          </div>
        </div>
        {(achievement.description || isPlaceholder) && (
          <p className="zc-achievement-card__description">
            {achievement.description || "Completa gli obiettivi richiesti per scoprire questo achievement."}
          </p>
        )}
        {metaItems.length > 0 && (
          <dl className="zc-achievement-card__meta">
            {metaItems.map(item => (
              <div key={item.label} className="zc-achievement-card__meta-item">
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </article>
  );
}


export default function StreamerProfilePanel({ profile, fallback }) {
  if (!profile) {
    return null;
  }

  const streamer = profile.streamer || fallback || {};
  const stats = profile.stats || {};
  const records = profile.records || {};
  const achievementsError = profile.achievementsError;
  const achievements = profile.achievements || {};
  const definitions = Array.isArray(achievements.definitions) ? achievements.definitions : [];

  const {
    all: allAchievements,
    unlocked: unlockedAchievements,
    locked: lockedAchievements
  } = normalizeAchievementsCollection(definitions);

  const totalUnlockedCount = achievements.totalUnlocked ?? unlockedAchievements.length;
  const totalAvailableCount = achievements.totalAvailable ?? allAchievements.length;
  const totalSessionsCount = profile.stats?.total_sessions ?? null;

  const summaryUnlockedDisplayRaw = formatCount(totalUnlockedCount);
  const hasSummaryFromApi = achievements.totalUnlocked != null;
  const hasAvailableFromApi = achievements.totalAvailable != null;
  const hasSessionsInfo = totalSessionsCount != null;
  const summaryUnlockedDisplay =
    (hasSummaryFromApi ? summaryUnlockedDisplayRaw : null) ??
    formatCount(unlockedAchievements.length) ??
    "0";
  const summaryAvailableDisplay =
    (hasAvailableFromApi ? formatCount(totalAvailableCount) : null) ??
    formatCount(allAchievements.length);
  const summarySessionsDisplay = hasSessionsInfo ? formatCount(totalSessionsCount) : null;
  const displayAchievements = unlockedAchievements.concat(lockedAchievements);
  const hasUnlocked = unlockedAchievements.length > 0;
  const hasLocked = lockedAchievements.length > 0;
  const shouldShowSummary =
    hasSummaryFromApi ||
    hasAvailableFromApi ||
    hasSessionsInfo ||
    hasUnlocked ||
    hasLocked;
  const shouldRenderAchievementsSection =
    hasUnlocked || hasLocked || shouldShowSummary || Boolean(achievementsError);

  const displayName =
    streamer.display_name ||
    streamer.username ||
    streamer.user_name ||
    streamer.twitch_username ||
    fallback?.username ||
    "Streamer";

  const avatarUrl =
    streamer.avatar ||
    streamer.profile_image_url ||
    streamer.avatar_url ||
    streamer.profile_pic_url ||
    streamer.profilePicUrl ||
    fallback?.avatar ||
    fallback?.profileImageUrl;

  const extraInfo = extractListFromObject({
    follower_count: streamer.followers || streamer.follower_count,
    total_views: streamer.total_views,
    language: streamer.language,
    country: streamer.country || streamer.location,
    last_stream_at: streamer.last_stream_at || streamer.last_live_at
  }).filter(item => item.value !== "-");

  const statEntries = extractListFromObject({
    total_sessions: stats.total_sessions ?? streamer.total_sessions,
    total_kills: stats.total_kills,
    average_kills: stats.average_kills,
    max_kills: stats.max_kills ?? stats.best_kills ?? fallback?.maxKills,
    total_survival_seconds:
      stats.total_survival_seconds ?? stats.total_survival_time,
    average_survival_seconds:
      stats.average_survival_seconds ?? stats.average_survival_time,
    best_survival_seconds:
      stats.best_survival_seconds ?? stats.best_survival_time ?? fallback?.bestSurvivalSeconds
  });

  const recordEntries = Object.entries(records || {})
    .map(([key, value]) => ({
      key,
      items: extractListFromObject(value, {
        filter: fieldKey => shouldIncludeRecordField(fieldKey)
      })
    }))
    .filter(entry => entry.items.length > 0);

  return (
    <div className="zc-profile">
      <div className="zc-profile__header">
        <div className="zc-profile__identity">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="zc-profile__avatar"
            />
          ) : (
            <div className="zc-profile__avatar placeholder" aria-hidden="true">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h3 className="zc-profile__name">{displayName}</h3>
            {streamer.twitch_url && (
              <a
                href={streamer.twitch_url}
                target="_blank"
                rel="noreferrer"
                className="zc-profile__link"
              >
                Guarda su Twitch
              </a>
            )}
            {streamer.bio && (
              <p className="zc-profile__bio">{streamer.bio}</p>
            )}
          </div>
        </div>
        {extraInfo.length > 0 && (
          <dl className="zc-profile__meta">
            {extraInfo.map(item => (
              <div key={item.label} className="zc-profile__meta-item">
                <dt>{titleFromKey(item.label)}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {statEntries.length > 0 && (
        <section className="zc-profile__section">
          <h4>Statistiche principali</h4>
          <div className="zc-profile__stats-grid">
            {statEntries.map(entry => (
              <article key={entry.label} className="zc-card">
                <header>{entry.label}</header>
                <strong>{entry.value}</strong>
              </article>
            ))}
          </div>
        </section>
      )}

      {recordEntries.length > 0 && (
        <section className="zc-profile__section">
          <h4>Record personali</h4>
          <div className="zc-profile__records">
            {recordEntries.map(entry => (
              <article key={entry.key} className="zc-card">
                <header>{titleFromKey(entry.key)}</header>
                <div className="zc-record-table">
                  {entry.items.map(item => (
                    <div key={item.label} className="zc-record-table__row">
                      <span className="zc-record-table__label">{item.label}</span>
                      <span className="zc-record-table__value">{item.value ?? "-"}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {shouldRenderAchievementsSection && (
        <section className="zc-profile__section">
          <h4>Achievements</h4>
          <div className="zc-profile__achievements">
            {(shouldShowSummary || achievementsError) && (
              <>
                {shouldShowSummary && (
                  <div className="zc-achievement-summary">
                    <span className="zc-achievement-summary__count">
                      Sbloccati:{' '}
                      <strong>{summaryUnlockedDisplay}</strong>
                      {summaryAvailableDisplay !== null && (
                        <span className="zc-achievement-summary__total">
                          / {summaryAvailableDisplay}
                        </span>
                      )}
                    </span>
                    {summarySessionsDisplay !== null && (
                      <span className="zc-achievement-summary__meta">
                        Sessioni totali: {summarySessionsDisplay}
                      </span>
                    )}
                  </div>
                )}
                {achievementsError && (
                  <p className="zc-achievement-summary__error" role="alert">
                    {achievementsError}
                  </p>
                )}
              </>
            )}

            {displayAchievements.length > 0 ? (
              <div className="zc-achievement-board" role="list">
                {unlockedAchievements.map((achievement, index) =>
                  renderAchievementCard(achievement, {
                    key: `achievement-unlocked-${achievement.code || index}`,
                    showUnlockedMeta: true
                  })
                )}
                {lockedAchievements.map((achievement, index) =>
                  renderAchievementCard(achievement, {
                    key: `achievement-locked-${achievement.code || index}`,
                    locked: true
                  })
                )}
              </div>
            ) : (
              <p className="zc-achievement-empty">
                Nessun achievement disponibile al momento.
              </p>
            )}

            {displayAchievements.length > 0 && unlockedAchievements.length === 0 && (
              <p className="zc-achievement-note">Questo streamer non ha ancora sbloccato achievements.</p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}



